import { useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { APPLICATIONS_ID, SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY } from "@/constants";
import useOpenGuestSignIn from "@/hooks/useOpenGuestSignIn";
import useAuth from "@/services/useAuth";

const getLoginBenefitsContent = (applicationId: APPLICATIONS_ID): JSX.Element[] => {
  const benefitsByApp: Partial<Record<APPLICATIONS_ID, JSX.Element[]>> = {
    [APPLICATIONS_ID.SpicyChat]: [
      <Trans i18nKey="auth:register.modal.benefits.uncensored" />,
      <Trans i18nKey="auth:register.modal.benefits.nsfw" />,
      <Trans i18nKey="auth:register.modal.benefits.favorites" />,
      <Trans i18nKey="auth:register.modal.benefits.createChatbots" />,
    ],
    [APPLICATIONS_ID.PixelChat]: [
      <Trans i18nKey="auth:register.modal.benefits.freeChats" />,
      <Trans i18nKey="auth:register.modal.benefits.fullLibrary" />,
      <Trans i18nKey="auth:register.modal.benefits.favorites" />,
      <Trans i18nKey="auth:register.modal.benefits.createChatbots" />,
    ],
    [APPLICATIONS_ID.SecretMate]: [
      <Trans i18nKey="auth:register.modal.benefits.freeChats" />,
      <Trans i18nKey="auth:register.modal.benefits.fullLibrary" />,
      <Trans i18nKey="auth:register.modal.benefits.favorites" />,
      <Trans i18nKey="auth:register.modal.benefits.createChatbots" />,
    ],
  };
  return benefitsByApp[applicationId] ?? [];
};

const MODAL_CLASSNAMES = {
  title: "text-center pl-[28px]",
};

function AccountRequiredModal() {
  const openGuestSignIn = useOpenGuestSignIn();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const applicationId = basicAppConfig.applicationId;
  const [stateIsVisible, setStateIsVisible] = useState(!isAuthenticated && !isLoading);
  const onCloseHandler = useCallback(() => {
    setStateIsVisible(false);
    navigate("/");
    localStorage.setItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY, "false");
  }, [navigate]);

  const displayLoginOverlay = useCallback(() => {
    setStateIsVisible(false);
    localStorage.setItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY, "true");
    openGuestSignIn();
  }, [openGuestSignIn]);

  const loginBenefits = getLoginBenefitsContent(applicationId as APPLICATIONS_ID);

  return (
    <>
      <Modal title={t("auth:register.modal.title")} open={stateIsVisible} onOpenChange={onCloseHandler} backdrop="blur" classNames={MODAL_CLASSNAMES}>
        <Row justify="center" className="pt-0">
          <Text variant="sm" type="pink-1000">
            <Trans i18nKey="auth:register.modal.subtitle" />
          </Text>
        </Row>
        <Col className="text-center">
          {!!loginBenefits.length && (
            <ul className="list-disc" onClick={displayLoginOverlay}>
              {loginBenefits.map((benefit, index) => (
                <li key={index}>
                  <Text type="neutral" className="cursor-pointer hover:text-secondary">
                    {benefit}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </Col>
        <Col className="text-center">
          <SpicyButton variant="secondary" color="primary_light" onPress={displayLoginOverlay}>
            <Trans i18nKey="auth:register.modal.cta.createAccount" />
          </SpicyButton>
          <Text variant={"md"} className="pt-6">
            <Trans i18nKey="auth:register.modal.footer.hasAccount" />{" "}
            <button onClick={displayLoginOverlay} className="hover:cursor-pointer bg-transparent text-link" aria-label={t("common:login")}>
              <Trans i18nKey="common:login" />
            </button>
          </Text>
        </Col>
      </Modal>
    </>
  );
}

export default AccountRequiredModal;
